class CustomHeaderMenu extends HTMLElement {
  constructor() {
    super();
    this.panelAnimationFrame = null;
    this.panelCloseTimer = null;
    this.handleClick = this.handleClick.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.handleFocusOut = this.handleFocusOut.bind(this);
    this.handleDocumentClick = this.handleDocumentClick.bind(this);
    this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
    this.handleResize = this.handleResize.bind(this);
  }

  connectedCallback() {
    if (this.isConnectedToMenu) return;

    this.isConnectedToMenu = true;
    this.addEventListener("click", this.handleClick);
    this.addEventListener("keydown", this.handleKeyDown);
    this.addEventListener("focusout", this.handleFocusOut);
    document.addEventListener("click", this.handleDocumentClick);
    document.addEventListener("visibilitychange", this.handleVisibilityChange);
    window.addEventListener("resize", this.handleResize);
    this.handleResize();
  }

  disconnectedCallback() {
    this.isConnectedToMenu = false;
    this.removeEventListener("click", this.handleClick);
    this.removeEventListener("keydown", this.handleKeyDown);
    this.removeEventListener("focusout", this.handleFocusOut);
    document.removeEventListener("click", this.handleDocumentClick);
    document.removeEventListener(
      "visibilitychange",
      this.handleVisibilityChange,
    );
    window.removeEventListener("resize", this.handleResize);
    this.cancelPanelAnimationFrame();
    this.cancelPanelCloseTimer();
    this.setHeaderOpenState(false);
  }

  get expandedTrigger() {
    return this.querySelector(
      '[data-custom-header-menu-trigger][aria-expanded="true"]',
    );
  }

  get surface() {
    return this.querySelector("[data-custom-header-menu-surface]");
  }

  handleClick(event) {
    if (!(event.target instanceof Element)) return;

    const trigger = event.target.closest(
      "[data-custom-header-menu-trigger]",
    );
    if (!(trigger instanceof HTMLButtonElement) || !this.contains(trigger)) {
      return;
    }

    const expandedTrigger = this.expandedTrigger;

    if (expandedTrigger === trigger) {
      this.closeAll();
      return;
    }

    this.open(trigger, expandedTrigger);
  }

  handleKeyDown(event) {
    if (event.key === "Tab") {
      this.handleTabKey(event);
      return;
    }

    if (event.key !== "Escape") return;

    const trigger = this.expandedTrigger;
    if (!(trigger instanceof HTMLButtonElement)) return;

    event.preventDefault();
    this.closeAll();
    trigger.focus();
  }

  handleTabKey(event) {
    const trigger = this.expandedTrigger;
    if (!(trigger instanceof HTMLButtonElement)) return;

    const panel = this.getPanel(trigger);
    if (!panel || panel.hidden) return;

    const focusableElements = this.getFocusableElements(panel);
    if (focusableElements.length === 0) return;

    const firstFocusable = focusableElements[0];
    const lastFocusable = focusableElements[focusableElements.length - 1];

    if (event.target === trigger && !event.shiftKey) {
      event.preventDefault();
      firstFocusable.focus();
      return;
    }

    if (event.target === firstFocusable && event.shiftKey) {
      event.preventDefault();
      trigger.focus();
      return;
    }

    if (event.target !== lastFocusable || event.shiftKey) return;

    const nextControl = this.getNextTopLevelControl(trigger);
    if (!nextControl) return;

    event.preventDefault();
    this.closeAll();
    nextControl.focus();
  }

  handleFocusOut(event) {
    if (
      event.relatedTarget instanceof Node &&
      this.contains(event.relatedTarget)
    ) {
      return;
    }

    this.closeAll();
  }

  handleDocumentClick(event) {
    if (event.target instanceof Node && this.contains(event.target)) return;
    this.closeAll();
  }

  handleVisibilityChange() {
    if (document.visibilityState === "hidden") {
      this.closeAll();
    }
  }

  handleResize() {
    const header = this.closest("#header-component");
    if (!header) return;

    const useDrawer =
      ("ontouchstart" in window && navigator.maxTouchPoints > 0) ||
      window.innerWidth < 990;
    const menuStyle = useDrawer ? "drawer" : "menu";

    if (header.dataset.menuStyle !== menuStyle) {
      header.dataset.menuStyle = menuStyle;
    }

    if (useDrawer && this.expandedTrigger) {
      const focusWasInDesktopMenu =
        document.activeElement instanceof Node &&
        this.contains(document.activeElement);

      this.closeAll();

      if (focusWasInDesktopMenu) {
        window.requestAnimationFrame(() => {
          const drawerTrigger = header.querySelector(
            ".menu-drawer-container > summary",
          );

          if (drawerTrigger instanceof HTMLElement) {
            drawerTrigger.focus();
          }
        });
      }
    }
  }

  open(trigger, previousTrigger = null) {
    const panel = this.getPanel(trigger);
    const surface = this.surface;
    if (!panel || !(surface instanceof HTMLElement)) return;

    this.cancelPanelCloseTimer();

    const sequence =
      surface.getAttribute("aria-hidden") === "false" ? "switch" : "opening";

    this.querySelectorAll("[data-custom-header-menu-panel]").forEach(
      (otherPanel) => {
        if (
          otherPanel instanceof HTMLElement &&
          otherPanel !== panel
        ) {
          otherPanel.hidden = true;
          otherPanel.setAttribute("inert", "");
          delete otherPanel.dataset.motion;
          delete otherPanel.dataset.sequence;
        }
      },
    );

    if (previousTrigger instanceof HTMLButtonElement) {
      previousTrigger.setAttribute("aria-expanded", "false");
    }

    trigger.setAttribute("aria-expanded", "true");
    panel.removeAttribute("inert");
    surface.setAttribute("aria-hidden", "false");
    surface.removeAttribute("inert");
    this.setHeaderOpenState(true);

    this.revealPanel(panel, sequence);
  }

  closeAll() {
    const panelsToHide = [];

    this.querySelectorAll(
      '[data-custom-header-menu-trigger][aria-expanded="true"]',
    ).forEach((trigger) => {
      if (trigger instanceof HTMLButtonElement) {
        trigger.setAttribute("aria-expanded", "false");
      }
    });

    this.querySelectorAll("[data-custom-header-menu-panel]").forEach(
      (panel) => {
        if (!(panel instanceof HTMLElement) || panel.hidden) return;

        panel.setAttribute("inert", "");
        panelsToHide.push(panel);
      },
    );

    const surface = this.surface;
    if (surface instanceof HTMLElement) {
      surface.setAttribute("aria-hidden", "true");
      surface.setAttribute("inert", "");
    }

    this.cancelPanelAnimationFrame();
    this.setHeaderOpenState(false);
    this.hidePanelsAfterClose(panelsToHide);
  }

  revealPanel(panel, sequence) {
    this.cancelPanelAnimationFrame();

    if (this.prefersReducedMotion) {
      delete panel.dataset.sequence;
      panel.dataset.motion = "active";
      panel.hidden = false;
      return;
    }

    panel.dataset.sequence = sequence;
    panel.dataset.motion = "entering";
    panel.hidden = false;
    this.panelAnimationFrame = window.requestAnimationFrame(() => {
      this.panelAnimationFrame = window.requestAnimationFrame(() => {
        this.panelAnimationFrame = null;

        if (!panel.hidden && panel.dataset.motion === "entering") {
          panel.dataset.motion = "active";
        }
      });
    });
  }

  cancelPanelAnimationFrame() {
    if (this.panelAnimationFrame === null) return;

    window.cancelAnimationFrame(this.panelAnimationFrame);
    this.panelAnimationFrame = null;
  }

  hidePanelsAfterClose(panels) {
    this.cancelPanelCloseTimer();

    const hidePanels = () => {
      this.panelCloseTimer = null;

      panels.forEach((panel) => {
        if (!panel.hasAttribute("inert")) return;

        panel.hidden = true;
        delete panel.dataset.motion;
        delete panel.dataset.sequence;
      });
    };

    if (this.prefersReducedMotion) {
      hidePanels();
      return;
    }

    this.panelCloseTimer = window.setTimeout(hidePanels, 200);
  }

  cancelPanelCloseTimer() {
    if (this.panelCloseTimer === null) return;

    window.clearTimeout(this.panelCloseTimer);
    this.panelCloseTimer = null;
  }

  getFocusableElements(panel) {
    const selector = [
      "a[href]",
      "button:not([disabled])",
      "input:not([disabled])",
      "select:not([disabled])",
      "textarea:not([disabled])",
      '[tabindex]:not([tabindex="-1"])',
    ].join(",");

    return Array.from(panel.querySelectorAll(selector)).filter(
      (element) =>
        element instanceof HTMLElement &&
        !element.closest("[hidden], [inert]"),
    );
  }

  getNextTopLevelControl(trigger) {
    const controls = Array.from(
      this.querySelectorAll(
        ".custom-header-menu__triggers .custom-header-menu__trigger",
      ),
    ).filter((element) => element instanceof HTMLElement);
    const triggerIndex = controls.indexOf(trigger);

    return triggerIndex >= 0 ? controls[triggerIndex + 1] ?? null : null;
  }

  get prefersReducedMotion() {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  }

  getPanel(trigger) {
    const panelId = trigger.getAttribute("aria-controls");
    if (!panelId) return null;

    const panel = document.getElementById(panelId);
    return panel instanceof HTMLElement && this.contains(panel) ? panel : null;
  }

  setHeaderOpenState(isOpen) {
    if (isOpen) {
      this.setAttribute("data-menu-open", "");
    } else {
      this.removeAttribute("data-menu-open");
    }

    const header = this.closest("#header-component");
    if (header) {
      if (isOpen) {
        header.setAttribute("data-custom-menu-open", "");
      } else {
        header.removeAttribute("data-custom-menu-open");
      }
    }

    const row = this.closest(".header__row");
    if (row) {
      row.classList.toggle("custom-header-menu-row--open", isOpen);
    }
  }
}

if (!customElements.get("custom-header-menu")) {
  customElements.define("custom-header-menu", CustomHeaderMenu);
}
